/**
 * Pay periods (BON-246) — /api/v1/pay-periods.
 *
 * The calendar is contiguous by construction: a new period extends it from one
 * end or the other, and the server's 400 names the exact date that would work.
 * The client doesn't second-guess that — it surfaces the message.
 *
 * Shapes mirror PayPeriodEntry in backend/src/services/pay-periods.service.ts.
 */
import { apiFetch } from '../lib/apiFetch';

export interface PayPeriodEntry {
  id: string;
  /** `YYYY-MM-DD`, inclusive. */
  startsOnIso: string;
  /** `YYYY-MM-DD`, inclusive — a shift on this date belongs to this period. */
  endsOnIso: string;
  payDateIso: string;
  /** True once the pay date has passed; editing then needs acknowledgement. */
  isPast: boolean;
}

export interface PayPeriodInput {
  startsOn: string;
  endsOn: string;
  payDate: string;
}

async function parsePeriod(res: Response, fallback: string): Promise<PayPeriodEntry> {
  let body: { period?: PayPeriodEntry; message?: string } | null = null;
  try {
    body = (await res.json()) as { period?: PayPeriodEntry; message?: string };
  } catch {
    body = null;
  }
  if (!res.ok || !body?.period) {
    throw new Error(body?.message ?? `${fallback} (HTTP ${res.status})`);
  }
  return body.period;
}

export async function fetchPayPeriods(): Promise<PayPeriodEntry[]> {
  const res = await apiFetch('/api/v1/pay-periods');
  if (!res.ok) throw new Error(`Failed to load pay periods (HTTP ${res.status})`);
  return ((await res.json()) as { periods: PayPeriodEntry[] }).periods;
}

export async function createPayPeriod(input: PayPeriodInput): Promise<PayPeriodEntry> {
  const res = await apiFetch('/api/v1/pay-periods', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  return parsePeriod(res, 'Failed to create the pay period');
}

/** `acknowledgePastPeriod` is required by the server to edit a period whose
 *  pay date has passed — it regroups pay that has already been presented. */
export async function updatePayPeriod(
  id: string,
  input: PayPeriodInput & { acknowledgePastPeriod?: boolean },
): Promise<PayPeriodEntry> {
  const res = await apiFetch(`/api/v1/pay-periods/${id}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return parsePeriod(res, 'Failed to update the pay period');
}

export async function deletePayPeriod(id: string): Promise<void> {
  const res = await apiFetch(`/api/v1/pay-periods/${id}`, { method: 'DELETE' });
  if (res.ok) return;
  let message: string | undefined;
  try {
    message = ((await res.json()) as { message?: string }).message;
  } catch {
    message = undefined;
  }
  throw new Error(message ?? `Failed to remove the pay period (HTTP ${res.status})`);
}

/** The gap between the calendar's end and a new period — what the admin form
 *  pre-fills so the common case needs no arithmetic from them. */
export function nextStartAfter(periods: PayPeriodEntry[]): string | null {
  if (periods.length === 0) return null;
  const last = periods.reduce((a, b) => (a.endsOnIso > b.endsOnIso ? a : b));
  const d = new Date(`${last.endsOnIso}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}
